import { useState, useMemo, useRef, useDeferredValue } from 'react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  size,
  useClick,
  useDismiss,
  useInteractions,
  FloatingPortal,
} from '@floating-ui/react'
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  VStack,
  HStack,
  Text,
  Kbd,
  useColorModeValue,
} from '@chakra-ui/react'
import { SearchIcon, CloseIcon, TimeIcon } from '@chakra-ui/icons'
import { colors } from '../../theme'
import { STRINGS } from '../../resources'
import type { Vulnerability } from '../../types/vulnerability'
import styles from './SearchWithSuggestions.module.css'

interface SearchWithSuggestionsProps {
  value: string
  onChange: (value: string) => void
  onSubmit: (value: string) => void  // Called on Enter to create a search pill
  data: Vulnerability[]
  recentSearches: string[]
  onAddRecentSearch: (search: string) => void
}

interface Suggestion {
  type: 'recent' | 'cve' | 'package' | 'hint'
  value: string
  label: string
  highlight?: string
}

const MAX_SUGGESTIONS = 8
const MAX_RECENT = 5

// Common search hints to show when input is empty
const SEARCH_HINTS = [
  STRINGS.SEARCH.HINTS.CVE,
  STRINGS.SEARCH.HINTS.PACKAGE,
  STRINGS.SEARCH.HINTS.DESCRIPTION,
]

export function SearchWithSuggestions({
  value,
  onChange,
  onSubmit,
  data,
  recentSearches,
  onAddRecentSearch,
}: SearchWithSuggestionsProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)

  // Defer suggestion computation to avoid blocking UI
  const deferredValue = useDeferredValue(value)

  // Floating UI setup
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          })
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  })

  const click = useClick(context, { toggle: false })
  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss])

  const dropdownBg = useColorModeValue(colors.ui.card.light, colors.ui.card.dark)
  const borderColor = useColorModeValue(colors.ui.border.light, colors.ui.border.dark)
  const hoverBg = useColorModeValue('blue.50', 'blue.900')
  const labelColor = useColorModeValue(colors.ui.text.secondary.light, colors.ui.text.secondary.dark)
  const highlightColor = useColorModeValue('blue.600', 'blue.300')
  const chipBg = useColorModeValue('gray.100', 'gray.700')

  // Pre-compute package counts (computed once from data, not on every keystroke)
  const packageCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const vuln of data) {
      counts[vuln.packageName] = (counts[vuln.packageName] || 0) + 1
    }
    return counts
  }, [data])

  // Compute suggestions based on input (uses deferred value to avoid blocking)
  const suggestions = useMemo((): Suggestion[] => {
    const results: Suggestion[] = []
    const query = deferredValue.toLowerCase().trim()

    // If empty, show recent searches + hints
    if (!query) {
      // Add recent searches
      recentSearches.slice(0, MAX_RECENT).forEach((search) => {
        results.push({
          type: 'recent',
          value: search,
          label: search,
        })
      })

      // Add hints if we have space
      if (results.length < MAX_SUGGESTIONS) {
        SEARCH_HINTS.forEach((hint) => {
          if (results.length < MAX_SUGGESTIONS) {
            results.push({
              type: 'hint',
              value: hint.value,
              label: hint.label,
              highlight: hint.example,
            })
          }
        })
      }

      return results
    }

    // Add matching recent searches first
    recentSearches
      .filter((s) => s.toLowerCase().includes(query))
      .slice(0, 2)
      .forEach((search) => {
        results.push({
          type: 'recent',
          value: search,
          label: search,
        })
      })

    // Find matching CVEs and packages - break early once we have enough
    const matchingCves = new Set<string>()
    const matchingPackages = new Set<string>()
    const maxCves = 3
    const maxPackages = 3

    for (const vuln of data) {
      // Break early if we have enough of everything
      if (matchingCves.size >= maxCves && matchingPackages.size >= maxPackages) break

      // Match CVEs
      if (matchingCves.size < maxCves && vuln.cve.toLowerCase().includes(query) && !matchingCves.has(vuln.cve)) {
        matchingCves.add(vuln.cve)
        results.push({
          type: 'cve',
          value: vuln.cve,
          label: vuln.cve,
          highlight: vuln.severity,
        })
      }

      // Match packages (use pre-computed counts instead of filtering)
      if (matchingPackages.size < maxPackages && vuln.packageName.toLowerCase().includes(query) && !matchingPackages.has(vuln.packageName)) {
        matchingPackages.add(vuln.packageName)
        results.push({
          type: 'package',
          value: vuln.packageName,
          label: vuln.packageName,
          highlight: `${packageCounts[vuln.packageName] || 0} vulnerabilities`,
        })
      }
    }

    return results.slice(0, MAX_SUGGESTIONS)
  }, [deferredValue, data, recentSearches, packageCounts])

  // Popular packages (computed once from data)
  const popularPackages = useMemo(() => {
    return Object.entries(packageCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }))
  }, [packageCounts])

  const handleSelect = (suggestion: Suggestion) => {
    if (suggestion.type === 'hint') {
      // For hints, just fill the input but don't submit
      onChange(suggestion.value)
    } else {
      // For actual suggestions, create a search pill
      onSubmit(suggestion.value)
      onAddRecentSearch(suggestion.value)
    }
    setIsOpen(false)
    setSelectedIndex(-1)
    inputRef.current?.blur()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle Enter regardless of dropdown state
    if (e.key === 'Enter') {
      e.preventDefault()
      if (isOpen && selectedIndex >= 0 && suggestions[selectedIndex]) {
        handleSelect(suggestions[selectedIndex])
      } else if (value.trim()) {
        onSubmit(value.trim())  // Create search pill
        onAddRecentSearch(value.trim())
        setIsOpen(false)
      }
      return
    }

    // Handle Escape regardless of dropdown state
    if (e.key === 'Escape') {
      setIsOpen(false)
      setSelectedIndex(-1)
      return
    }

    // For arrow keys, need dropdown open with suggestions
    if (!isOpen || suggestions.length === 0) {
      if (e.key === 'ArrowDown') {
        setIsOpen(true)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1))
        break
      case ' ':
        // Allow space to be typed normally - stop floating-ui from treating it as click
        e.stopPropagation()
        return
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
    setSelectedIndex(-1)
    if (!isOpen) setIsOpen(true)
  }

  const handleClear = () => {
    onChange('')
    inputRef.current?.focus()
  }

  const getTypeIcon = (type: Suggestion['type']) => {
    switch (type) {
      case 'recent':
        return <TimeIcon boxSize={3} color={labelColor} />
      case 'cve':
        return <Text fontSize="xs" fontWeight="bold" color="red.500">CVE</Text>
      case 'package':
        return <Text fontSize="xs" fontWeight="bold" color="purple.500">PKG</Text>
      case 'hint':
        return <Text fontSize="xs" color={labelColor}>💡</Text>
    }
  }

  return (
    <Box position="relative" w={{ base: '100%', md: '400px' }}>
      <InputGroup ref={refs.setReference} className="search-glow" borderRadius="md" {...getReferenceProps()}>
        <InputLeftElement>
          <SearchIcon color="gray.400" />
        </InputLeftElement>
        <Input
          ref={inputRef}
          placeholder={STRINGS.FILTERS.SEARCH_PLACEHOLDER}
          value={value}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          pr={value ? '2.5rem' : undefined}
        />
        {value && (
          <InputRightElement>
            <IconButton
              aria-label={STRINGS.SEARCH.CLEAR_SEARCH}
              icon={<CloseIcon boxSize={2} />}
              size="xs"
              variant="ghost"
              onClick={handleClear}
            />
          </InputRightElement>
        )}
      </InputGroup>

      {/* Dropdown - rendered in FloatingPortal to avoid z-index issues */}
      {isOpen && suggestions.length > 0 && (
        <FloatingPortal>
          <Box
            ref={refs.setFloating}
            className={styles.dropdown}
            style={floatingStyles}
            {...getFloatingProps()}
            bg={dropdownBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
            shadow="lg"
            overflow="hidden"
            zIndex={1400}
          >
          <VStack align="stretch" spacing={0} py={1}>
            {/* Section headers */}
            {!value && recentSearches.length > 0 && (
              <Text px={3} py={1} fontSize="xs" fontWeight="semibold" color={labelColor} textTransform="uppercase">
                {STRINGS.SEARCH.RECENT_SEARCHES}
              </Text>
            )}

            {suggestions.map((suggestion, index) => {
              // Show section header for hints
              const isFirstHint = suggestion.type === 'hint' &&
                (index === 0 || suggestions[index - 1]?.type !== 'hint')

              return (
                <Box key={`${suggestion.type}-${suggestion.value}-${index}`}>
                  {isFirstHint && !value && (
                    <Text px={3} py={1} fontSize="xs" fontWeight="semibold" color={labelColor} textTransform="uppercase" mt={2}>
                      {STRINGS.SEARCH.TRY_SEARCHING}
                    </Text>
                  )}
                  <HStack
                    px={3}
                    py={2}
                    cursor="pointer"
                    bg={selectedIndex === index ? hoverBg : 'transparent'}
                    _hover={{ bg: hoverBg }}
                    onClick={() => handleSelect(suggestion)}
                    spacing={3}
                  >
                    <Box w={6} textAlign="center">
                      {getTypeIcon(suggestion.type)}
                    </Box>
                    <VStack align="start" spacing={0} flex={1}>
                      <Text
                        fontSize="sm"
                        fontWeight={suggestion.type === 'cve' ? 'medium' : 'normal'}
                        color={suggestion.type === 'hint' ? labelColor : undefined}
                      >
                        {suggestion.type === 'hint' ? suggestion.label : suggestion.value}
                      </Text>
                      {suggestion.highlight && (
                        <Text fontSize="xs" color={suggestion.type === 'hint' ? highlightColor : labelColor}>
                          {suggestion.type === 'hint' ? `e.g., ${suggestion.highlight}` : suggestion.highlight}
                        </Text>
                      )}
                    </VStack>
                  </HStack>
                </Box>
              )
            })}

            {/* Popular packages when no input */}
            {!value && popularPackages.length > 0 && (
              <>
                <Text px={3} py={1} fontSize="xs" fontWeight="semibold" color={labelColor} textTransform="uppercase" mt={2}>
                  {STRINGS.SEARCH.POPULAR_PACKAGES}
                </Text>
                <HStack px={3} py={2} flexWrap="wrap" gap={1}>
                  {popularPackages.map((pkg) => (
                    <Box
                      key={pkg.name}
                      px={2}
                      py={0.5}
                      bg={chipBg}
                      borderRadius="full"
                      fontSize="xs"
                      cursor="pointer"
                      _hover={{ bg: hoverBg }}
                      onClick={() => {
                        onChange(pkg.name)
                        onAddRecentSearch(pkg.name)
                        setIsOpen(false)
                      }}
                    >
                      {pkg.name} ({pkg.count})
                    </Box>
                  ))}
                </HStack>
              </>
            )}
          </VStack>

          {/* Keyboard hint */}
          <HStack
            px={3}
            py={2}
            borderTopWidth="1px"
            borderColor={borderColor}
            fontSize="xs"
            color={labelColor}
            justify="center"
            spacing={4}
          >
            <HStack spacing={1}>
              <Kbd size="sm">↑↓</Kbd>
              <Text>navigate</Text>
            </HStack>
            <HStack spacing={1}>
              <Kbd size="sm">Enter</Kbd>
              <Text>select</Text>
            </HStack>
            <HStack spacing={1}>
              <Kbd size="sm">Esc</Kbd>
              <Text>close</Text>
            </HStack>
          </HStack>
        </Box>
        </FloatingPortal>
      )}
    </Box>
  )
}
